import React, { useCallback } from "react";
import { fromJS, Set } from "immutable";
import IconButton from "material-ui/IconButton";
import Popover, { PopoverAnimationVertical } from "material-ui/Popover";
import { ProductCollectionHeaderSearch } from "./product-collection-header-search";
import SearchIcon from "components/icons/magnify-glass";
import { View } from "constants/product-collection";
import theme from "styles/theme";
import { useFetchRollupHashtags } from "containers/folders/hooks/server-rollups";

interface IProps {
    defaultSearchText: { value: string };
    hashtags: Set<string>;
    onSearchChange: (searchText, force?) => void;
    view: View;
    searchText?: string;
    isServerRollup: boolean;
    merchantId?: string;
    collectionId: string;
}

interface IState {
    anchorEl: any;
    open: boolean;
}

const SearchAssortments = ({
    defaultSearchText,
    hashtags,
    onSearchChange,
    view,
    searchText,
    isServerRollup,
    merchantId,
    collectionId,
}: IProps) => {
    const [state, setState] = React.useState<IState>({
        open: false,
        anchorEl: undefined,
    });
    const [hashtagSearchText, setHashtagSearchText] = React.useState<string>(null);
    const [hashtagSuggestions, setHashtagSuggestions] = React.useState<Set<string>>(hashtags);

    const handleClick = useCallback((event) => {
        setState({ open: true, anchorEl: event.currentTarget });
    }, []);

    const handleClose = useCallback(
        (_event) => {
            onSearchChange(searchText, true);
            setState({
                ...state,
                open: false,
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [onSearchChange]
    );

    useFetchRollupHashtags(collectionId, merchantId, hashtagSearchText, {
        enabled: isServerRollup && collectionId && merchantId && hashtagSearchText !== null,
        onSettled: (data) => {
            setHashtagSuggestions(fromJS(data));
        },
    });

    const handleTextFieldSearchChange = React.useCallback(
        (value) => {
            if (value === "" && hashtagSearchText) {
                setHashtagSuggestions(fromJS([]));
                setHashtagSearchText(null);
            }
            onSearchChange(value);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [hashtagSearchText]
    );

    const handleHashtagSearchChange = (value) => {
        setHashtagSearchText(value);
    };

    return (
        <React.Fragment>
            <div className="search__collapse">
                <IconButton onClick={handleClick}>
                    <SearchIcon color={theme.palette.grey} />
                </IconButton>
                <Popover
                    anchorEl={state.anchorEl}
                    anchorOrigin={{ horizontal: "left", vertical: "top" }}
                    animation={PopoverAnimationVertical}
                    autoCloseWhenOffScreen={false}
                    onRequestClose={handleClose}
                    open={state.open}
                    style={{ padding: "12px" }}
                    targetOrigin={{ horizontal: "left", vertical: "top" }}
                >
                    <ProductCollectionHeaderSearch
                        defaultSearchText={defaultSearchText}
                        hashtags={hashtagSuggestions}
                        onHashtagSearchChange={handleHashtagSearchChange}
                        onChange={handleTextFieldSearchChange}
                        view={view}
                        isServerRollup={isServerRollup}
                    />
                </Popover>
            </div>
            <div className="search__expand">
                <ProductCollectionHeaderSearch
                    defaultSearchText={defaultSearchText}
                    hashtags={hashtagSuggestions}
                    onHashtagSearchChange={handleHashtagSearchChange}
                    onChange={handleTextFieldSearchChange}
                    view={view}
                    isServerRollup={isServerRollup}
                />
            </div>
        </React.Fragment>
    );
};

// separate export statement so function name shows up in React DevTools
export { SearchAssortments };
